import React, {useMemo, useState} from "react";
import {useFormikContext} from "formik";
import {styled, Text} from "@reside/ui";

import {
  MenuCategory,
  MenuContainer,
  MenuItemContainer,
} from "../../../../atoms/menu";
import {useFacilityWorkflowsQuery} from "../../../../atoms/start-workflow/hooks/useFacilityWorkflowsQuery";
import {useFacilitySettingsParams} from "../../hooks/useFacilitySettingsParams";
import {useScrollSpyContext} from "../../../../hooks/useScrollSpy";
import {
  SidebarNav,
  TemplateSelectorWrapper,
  TemplateLabel,
  TemplateCodeButton,
} from "./styles";
import {parseWorkflowSectionId} from "./workflowSectionIds";
import {
  filterVisibleFacilitySettingsBlocks,
  getFacilitySettingsBlockLabel,
} from "./facilitySettingsBlocks";
import {WorkflowSideNavSubItems} from "./WorkflowSideNavSubItems";
import {SECTION_WORKFLOW_TRIGGERS} from "../workflow-triggers-section";
import {FacilitySettingsVariables} from "../../../../services/FacilitySettingsService";

export const SECTION_PCC = "__pcc";
export const SECTION_PCC_INTEGRATION = "__pcc-integration";

type Props = Readonly<{
  blocks: ReadonlyArray<any>;
  pccTemplateBlocks?: ReadonlyArray<any>;
  templateName: string;
  isResideAdmin: boolean;
  isReadOnly?: boolean;
  onOpenChangeTemplate: () => void;
}>;

export const FacilitySettingsSideNav = ({
  blocks,
  pccTemplateBlocks = [],
  templateName,
  isResideAdmin,
  isReadOnly = false,
  onOpenChangeTemplate,
}: Props) => {
  const {values} = useFormikContext<FacilitySettingsVariables>();
  const visibleBlocks = useMemo(
    () => filterVisibleFacilitySettingsBlocks(blocks, values),
    [blocks, values],
  );
  const visiblePccTemplateBlocks = useMemo(
    () => filterVisibleFacilitySettingsBlocks(pccTemplateBlocks, values),
    [pccTemplateBlocks, values],
  );
  const {facilityId} = useFacilitySettingsParams();
  const {data: facilityWorkflows, isLoading: isWorkflowsLoading} =
    useFacilityWorkflowsQuery(facilityId);
  const workflows = facilityWorkflows?.workflows ?? [];
  const showWorkflowsSection = isWorkflowsLoading || workflows.length > 0;
  const showWorkflowTriggersSection = workflows.length > 1;
  const {activeId, setActiveId} = useScrollSpyContext();

  const activeWorkflowType = parseWorkflowSectionId(activeId);

  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<string>>(
    () => new Set(activeWorkflowType ? [activeWorkflowType] : []),
  );

  const handleSelectItem = (itemId: string) => {
    document
      .getElementById(itemId)
      ?.scrollIntoView({behavior: "smooth", block: "start"});
    setActiveId(itemId);

    const workflowType = parseWorkflowSectionId(itemId);
    if (workflowType) {
      setExpandedWorkflows(
        prev => new Set([...Array.from(prev), workflowType]),
      );
    }
  };

  const toggleWorkflow = (type: string) => {
    setExpandedWorkflows(prev => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  };

  return (
    <SidebarNav>
      {isResideAdmin && (
        <TemplateSelectorWrapper>
          <TemplateLabel>Facility Template</TemplateLabel>
          <TemplateCodeButton
            type="button"
            onClick={onOpenChangeTemplate}
            disabled={isReadOnly}
          >
            {templateName}
          </TemplateCodeButton>
        </TemplateSelectorWrapper>
      )}

      <FullWidthMenuContainer>
        <MenuCategory iconName="building" title="General" />
        <CategoryItems>
          {visibleBlocks.map(block => {
            const isActive = activeId === block.id;
            return (
              <CompactMenuItemContainer
                key={block.id}
                onClick={() => handleSelectItem(block.id)}
              >
                <Text
                  fontWeight={isActive ? "bold" : "normal"}
                  lineHeight="18px"
                  color={isActive ? "primary" : "darkBlue100"}
                >
                  {getFacilitySettingsBlockLabel(block)}
                </Text>
              </CompactMenuItemContainer>
            );
          })}
        </CategoryItems>
      </FullWidthMenuContainer>

      {isResideAdmin && (
        <FullWidthMenuContainer>
          <MenuCategory iconName="arrows-cw" title="PCC Integration" />
          <CategoryItems>
            {visiblePccTemplateBlocks.map(block => {
              const blockId = String(block.id);
              const isActive = activeId === blockId;

              return (
                <CompactMenuItemContainer
                  key={blockId}
                  onClick={() => handleSelectItem(blockId)}
                >
                  <Text
                    fontWeight={isActive ? "bold" : "normal"}
                    lineHeight="18px"
                    color={isActive ? "primary" : "darkBlue100"}
                  >
                    {getFacilitySettingsBlockLabel(block)}
                  </Text>
                </CompactMenuItemContainer>
              );
            })}
            <CompactMenuItemContainer
              onClick={() => handleSelectItem(SECTION_PCC)}
            >
              <Text
                fontWeight={activeId === SECTION_PCC ? "bold" : "normal"}
                lineHeight="18px"
                color={activeId === SECTION_PCC ? "primary" : "darkBlue100"}
              >
                PointClickCare Connection Settings
              </Text>
            </CompactMenuItemContainer>
          </CategoryItems>
        </FullWidthMenuContainer>
      )}

      {showWorkflowsSection && (
        <FullWidthMenuContainer>
          <MenuCategory iconName="th-list" title="Workflows" />
          <CategoryItems>
            {isWorkflowsLoading ? (
              <LoadingLabel>Loading…</LoadingLabel>
            ) : (
              workflows.map(workflow => {
                const isExpanded = expandedWorkflows.has(workflow.type);
                const isWorkflowActive = activeWorkflowType === workflow.type;

                return (
                  <WorkflowAccordionItem key={workflow.type}>
                    <WorkflowHeader
                      type="button"
                      onClick={() => toggleWorkflow(workflow.type)}
                    >
                      <Text
                        fontSize="14px"
                        fontWeight={isWorkflowActive ? "bold" : "normal"}
                        lineHeight="18px"
                        color={isWorkflowActive ? "primary" : "darkBlue100"}
                      >
                        {workflow.label}
                      </Text>
                      <Chevron $open={isExpanded}>▾</Chevron>
                    </WorkflowHeader>

                    {isExpanded && (
                      <WorkflowSubItems>
                        <WorkflowSideNavSubItems
                          workflowType={workflow.type}
                          workflowLabel={workflow.label}
                          activeId={activeId}
                          onSelectItem={handleSelectItem}
                        />
                      </WorkflowSubItems>
                    )}
                  </WorkflowAccordionItem>
                );
              })
            )}
          </CategoryItems>
        </FullWidthMenuContainer>
      )}

      {isResideAdmin && showWorkflowTriggersSection && (
        <FullWidthMenuContainer>
          <MenuCategory iconName="th-list" title="Workflow Triggers" />
          <CategoryItems>
            <CompactMenuItemContainer
              onClick={() => handleSelectItem(SECTION_WORKFLOW_TRIGGERS)}
            >
              <Text
                fontWeight={
                  activeId === SECTION_WORKFLOW_TRIGGERS ? "bold" : "normal"
                }
                lineHeight="18px"
                color={
                  activeId === SECTION_WORKFLOW_TRIGGERS
                    ? "primary"
                    : "darkBlue100"
                }
              >
                Manage Triggers
              </Text>
            </CompactMenuItemContainer>
          </CategoryItems>
        </FullWidthMenuContainer>
      )}
    </SidebarNav>
  );
};

const FullWidthMenuContainer = styled(MenuContainer)`
  width: 100%;
`;

const CategoryItems = styled.div`
  margin-top: 16px;
  margin-left: 32px;
`;

const CompactMenuItemContainer = styled(MenuItemContainer)`
  margin-top: 6px;
`;

const WorkflowAccordionItem = styled.div`
  margin-top: 6px;
`;

const WorkflowHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border: none;
  padding: 0 15px;
  cursor: pointer;
  text-align: left;
  gap: 8px;
  font-family: inherit;
  font-size: inherit;

  &:hover {
    opacity: 0.5;
  }
`;

const Chevron = styled.span<{$open: boolean}>`
  font-size: 12px;
  transform: ${({$open}) => ($open ? "rotate(0deg)" : "rotate(-90deg)")};
  transition: transform 0.18s;
  display: inline-block;
  flex-shrink: 0;
  opacity: 0.4;
`;

const WorkflowSubItems = styled.div`
  margin-top: 4px;
  margin-bottom: 4px;
`;

const LoadingLabel = styled.p`
  margin: 0;
  padding: 0 15px;
  font-size: 14px;
  line-height: 18px;
  color: ${({theme}) => theme.color.darkBlue100};
  opacity: 0.5;
  font-style: italic;
`;
